<template>
  <div :class="backgroundClasses">
    <!-- D-237 (A4): this overlay had NOTHING — no role, no ESC, no trap, no focus
         management. The dialog is the WRAPPER, not the `.background` backdrop, so the
         backdrop is not inside the named region.
         🪤 It is class-toggled (`.background.visible`), not `v-if`-mounted, which is
         exactly why the trap keys on a `shown` predicate: a trap armed at `mounted`
         would arm at player load, while this subtree is `display: none`. -->
    <div
      class="hashtag-modal-wrapper"
      ref="dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hashtag-modal-title"
      tabindex="-1"
    >
      <div class="title-row">
        <!-- 🪤 An `<h1>` INSIDE AN IFRAME is a claim about the HOST page's outline, which
             the widget does not own and cannot see — AT merges the two. Demoted to `<h2>`
             per the plan's recommended shape; the full headings decision is D-248.
             Here `aria-labelledby` IS right, unlike the other three overlays: there is a
             real title element and it names the whole dialog. -->
        <h2 id="hashtag-modal-title">Hashtags</h2>
        <button v-on:click="onPressClose" aria-label="Close hashtags">
          <img :src="closeImgSrc" alt="">
        </button>
      </div>
      <div class="tags-wrapper">
        <hash-tag-list
          :force-show-more="forceShowMore"
          :theme="theme"
          :tags="tags"
        ></hash-tag-list>
      </div>
    </div>
  </div>
</template>

<script>
import HashTagList from './HashTagList.vue';
// Vue1 `require('...svg')` -> ES-module import so Vite resolves the asset
// (inlined as a data URI under the build's assetsInlineLimit).
import closeSvg from '../../assets/v3-assets/close.svg';
import { createFocusTrap } from '../../shared-components/focusTrap';

export default {
  name: 'hashtag-modal',
  // D-237 (A4): keyed on the prop that actually toggles it, not on `mounted`.
  mixins: [createFocusTrap({
    shown() { return this.isVisible; },
    close() { this.onPressClose(); },
  })],
  data() {
    return {
      forceShowMore: true,
    };
  },
  computed: {
    closeImgSrc() {
      return closeSvg;
    },
    backgroundClasses() {
      if (this.isVisible) {
        return 'background visible';
      }

      return 'background';
    },
  },
  components: { HashTagList },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    onPressClose: {
      type: Function,
      default: () => {},
    },
    theme: {
      type: Object,
      validator(value) {
        return ['light', 'dark', 'custom'].includes(value.type);
      },
    },
    tags: {
      type: Array,
      default() {
        return [
          { id: 1, name: 'label', station: 'WPHM' },
          { id: 2, name: 'label2', station: 'WPHM' },
          { id: 3, name: 'label3', station: 'WPHM' },
        ];
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.background {
  background-color: #E5E5E5;
  padding: 16px;
  display: none;
  z-index: 100;
  position: fixed;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 100%;
}

.background.visible {
  display: flex;
  justify-content: center;
  align-items: center;
}

.hashtag-modal-wrapper {
  font-family: sans-serif;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0px 1px 3px rgba(34, 32, 70, 0.2);
  height: 100%;
  overflow: hidden;
  width: 100%;

  .title-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 18px;

    // D-237 (A4): was `h1`, demoted to `h2` — see the template. Same rules, same
    // rendering: both are block, and the size comes from the UA sheet, so `font-size`
    // is pinned here to keep the visual identical to the `<h1>` it replaces.
    h2 {
      font-weight: 500;
      font-size: 2em;
      margin: 0px;
      padding: 0px;
    }
    button {
      border: none;
      background: none;

      &:hover {
        cursor: pointer;
      }
    }
  }
}

.tags-wrapper {
  margin: 0px;
  height: 80%;
  overflow: auto;
  overflow-x: hidden;
}
</style>
